import React, { useState, useEffect } from 'react';
import { 
  Users, 
  CreditCard, 
  Calendar, 
  TrendingUp, 
  AlertTriangle,
  Plus,
  Minus,
  History,
  Eye,
  DollarSign,
  X
} from 'lucide-react';
import axios from 'axios';
import Card from '../ui/Card';
import Table from '../ui/Table';
import Button from '../ui/Button';
import Modal from '../ui/Modal';
import FormField from '../ui/FormField';

const SubscriptionManagementTab = ({ settings }) => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedUser, setSelectedUser] = useState(null);
  const [showCreditHistory, setShowCreditHistory] = useState(false);
  const [showAdjustCredits, setShowAdjustCredits] = useState(false);
  const [showCancelConfirmation, setShowCancelConfirmation] = useState(false);
  const [creditHistory, setCreditHistory] = useState([]);
  const [creditSummary, setCreditSummary] = useState(null);
  const [adjustmentForm, setAdjustmentForm] = useState({
    amount: '',
    reason: ''
  });
  const [cancellingSubscription, setCancellingSubscription] = useState(false);

  const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || '';

  const fetchSubscriptionSummary = async () => {
    try {
      setLoading(true);
      const adminToken = localStorage.getItem('admin_token');
      if (!adminToken) {
        console.error('No admin token found');
        return;
      }
      
      const response = await axios.get(`${API_BASE_URL}/api/admin/subscription/subscription-summary`, {
        headers: {
          'Authorization': `Bearer ${adminToken}`
        }
      });
      setUsers(response.data);
    } catch (error) {
      console.error('Error fetching subscription summary:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchCreditHistory = async (userId) => {
    try {
      const adminToken = localStorage.getItem('admin_token');
      if (!adminToken) {
        console.error('No admin token found');
        return;
      }
      
      const headers = {
        'Authorization': `Bearer ${adminToken}`
      };
      
      const [historyResponse, summaryResponse] = await Promise.all([
        axios.get(`${API_BASE_URL}/api/admin/subscription/users/${userId}/credit-history`, { headers }),
        axios.get(`${API_BASE_URL}/api/admin/subscription/users/${userId}/credit-summary`, { headers })
      ]);
      
      setCreditHistory(historyResponse.data);
      setCreditSummary(summaryResponse.data);
    } catch (error) {
      console.error('Error fetching credit history:', error);
    }
  };

  const handleViewCreditHistory = async (user) => {
    setSelectedUser(user);
    await fetchCreditHistory(user.user_id);
    setShowCreditHistory(true);
  };

  const handleAdjustCredits = (user) => {
    setSelectedUser(user);
    setAdjustmentForm({ amount: '', reason: '' });
    setShowAdjustCredits(true);
  };

  const handleCancelSubscription = (user) => {
    setSelectedUser(user);
    setShowCancelConfirmation(true);
  };

  const confirmCancelSubscription = async () => {
    try {
      setCancellingSubscription(true);
      const adminToken = localStorage.getItem('admin_token');
      if (!adminToken) {
        alert('Authentication required. Please log in again.');
        return;
      }

      const response = await axios.post(
        `${API_BASE_URL}/api/admin/subscription/users/${selectedUser.user_id}/cancel-subscription`,
        {},
        {
          headers: {
            'Authorization': `Bearer ${adminToken}`
          }
        }
      );

      alert(`Successfully cancelled subscription for ${selectedUser.user_name}. ${response.data.forfeited_credits > 0 ? `${response.data.forfeited_credits} credits were forfeited.` : ''}`);
      setShowCancelConfirmation(false);
      await fetchSubscriptionSummary(); // Refresh data
    } catch (error) {
      console.error('Error cancelling subscription:', error);
      if (error.response?.status === 403) {
        alert('Access denied. Please check your admin permissions.');
      } else if (error.response?.status === 400) {
        alert(error.response.data.detail || 'No active subscription found to cancel.');
      } else {
        alert('Error cancelling subscription. Please try again.');
      }
    } finally {
      setCancellingSubscription(false);
    }
  };

  const submitCreditAdjustment = async () => {
    try {
      const adminToken = localStorage.getItem('admin_token');
      if (!adminToken) {
        alert('Authentication required. Please log in again.');
        return;
      }
      
      const adjustmentData = {
        user_id: selectedUser.user_id,
        amount: parseInt(adjustmentForm.amount),
        reason: adjustmentForm.reason,
        admin_user_id: 'current_admin' // This would be replaced with actual admin ID
      };

      await axios.post(`${API_BASE_URL}/api/admin/subscription/users/${selectedUser.user_id}/adjust-credits`, adjustmentData, {
        headers: {
          'Authorization': `Bearer ${adminToken}`
        }
      });
      
      alert('Credits adjusted successfully!');
      setShowAdjustCredits(false);
      await fetchSubscriptionSummary(); // Refresh data
    } catch (error) {
      console.error('Error adjusting credits:', error);
      if (error.response?.status === 403) {
        alert('Access denied. Please check your admin permissions.');
      } else {
        alert('Error adjusting credits. Please try again.');
      }
    }
  };

  useEffect(() => {
    fetchSubscriptionSummary();
  }, []);

  const getPlanTypeColor = (planType) => {
    switch (planType) {
      case 'trial':
        return { bg: '#fef3c7', text: '#d97706', border: '#fbbf24' };
      case 'monthly':
        return { bg: '#dbeafe', text: '#2563eb', border: '#3b82f6' };
      case 'annual':
        return { bg: '#d1fae5', text: '#059669', border: '#10b981' };
      default:
        return { bg: '#f3f4f6', text: '#6b7280', border: '#9ca3af' };
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-GB');
  };

  const formatAmount = (amount) => {
    if (amount == null || amount === undefined) return '0';
    return Math.abs(amount).toLocaleString();
  };

  const getTransactionIcon = (type) => {
    switch (type) {
      case 'credit_grant':
      case 'plan_assignment':
        return <Plus size={14} color="#10b981" />;
      case 'credit_deduction':
        return <Minus size={14} color="#ef4444" />;
      case 'credit_refund':
        return <TrendingUp size={14} color="#3b82f6" />;
      case 'manual_adjustment':
        return <DollarSign size={14} color="#f59e0b" />;
      default:
        return <CreditCard size={14} color="#6b7280" />;
    }
  };

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '40px' }}>
        <div>Loading subscription data...</div>
      </div>
    );
  }

  return (
    <div style={{ padding: '24px' }}>
      <div style={{ marginBottom: '24px' }}>
        <h2 style={{ 
          fontSize: '24px', 
          fontWeight: 'bold', 
          marginBottom: '8px',
          color: '#1f2937'
        }}>
          Subscription Management
        </h2>
        <p style={{ color: '#6b7280', fontSize: '14px' }}>
          Manage user subscriptions, credits, and plan assignments
        </p>
      </div>

      {/* Summary Cards */}
      <div style={{ 
        display: 'grid', 
        gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', 
        gap: '20px',
        marginBottom: '24px'
      }}>
        <Card>
          <Card.Content style={{ padding: '20px' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div>
                <p style={{ fontSize: '12px', color: '#6b7280', marginBottom: '4px' }}>Total Users</p>
                <p style={{ fontSize: '24px', fontWeight: 'bold', color: '#1f2937' }}>
                  {users.length}
                </p>
              </div>
              <Users size={32} color="#3b82f6" />
            </div>
          </Card.Content>
        </Card>

        <Card>
          <Card.Content style={{ padding: '20px' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div>
                <p style={{ fontSize: '12px', color: '#6b7280', marginBottom: '4px' }}>Trial Users</p>
                <p style={{ fontSize: '24px', fontWeight: 'bold', color: '#d97706' }}>
                  {users.filter(user => user.current_plan_type === 'trial').length}
                </p>
              </div>
              <Calendar size={32} color="#d97706" />
            </div>
          </Card.Content>
        </Card>

        <Card>
          <Card.Content style={{ padding: '20px' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div>
                <p style={{ fontSize: '12px', color: '#6b7280', marginBottom: '4px' }}>Total Credits</p>
                <p style={{ fontSize: '24px', fontWeight: 'bold', color: '#059669' }}>
                  {users.reduce((sum, user) => sum + user.credits_remaining, 0).toLocaleString()}
                </p>
              </div>
              <CreditCard size={32} color="#059669" />
            </div>
          </Card.Content>
        </Card>
      </div>

      {/* Users Table */}
      <Card>
        <Card.Header>
          <Card.Title>User Subscriptions</Card.Title>
        </Card.Header>
        <Card.Content>
          <Table>
            <Table.Header>
              <Table.Row>
                <Table.Head>User</Table.Head>
                <Table.Head>Plan</Table.Head>
                <Table.Head>Type</Table.Head>
                <Table.Head>Credits</Table.Head>
                <Table.Head>Used</Table.Head>
                <Table.Head>Assigned</Table.Head>
                <Table.Head>Actions</Table.Head>
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {users.map((user) => {
                const planColors = getPlanTypeColor(user.current_plan_type);
                const isTrialExpiring = user.trial_expires_at && 
                  new Date(user.trial_expires_at) < new Date(Date.now() + 7 * 24 * 60 * 60 * 1000);

                return (
                  <Table.Row key={user.user_id}>
                    <Table.Cell>
                      <div>
                        <div style={{ fontWeight: '500', fontSize: '14px' }}>
                          {user.user_name}
                        </div>
                        <div style={{ fontSize: '12px', color: '#6b7280' }}>
                          {user.user_email}
                        </div>
                        <div style={{ fontSize: '11px', color: '#9ca3af' }}>
                          {user.company_name}
                        </div>
                      </div>
                    </Table.Cell>
                    <Table.Cell>
                      <div style={{ fontWeight: '500', fontSize: '14px' }}>
                        {user.current_plan_name}
                      </div>
                      {user.trial_expires_at && (
                        <div style={{ fontSize: '11px', color: isTrialExpiring ? '#dc2626' : '#6b7280' }}>
                          Trial expires: {formatDate(user.trial_expires_at)}
                        </div>
                      )}
                    </Table.Cell>
                    <Table.Cell>
                      <span style={{
                        padding: '4px 8px',
                        borderRadius: '12px',
                        fontSize: '11px',
                        fontWeight: '500',
                        backgroundColor: planColors.bg,
                        color: planColors.text,
                        border: `1px solid ${planColors.border}`,
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '4px'
                      }}>
                        {isTrialExpiring && <AlertTriangle size={10} />}
                        {user.current_plan_type.toUpperCase()}
                      </span>
                    </Table.Cell>
                    <Table.Cell>
                      <span style={{ 
                        fontWeight: '500', 
                        color: user.credits_remaining < 100 ? '#dc2626' : '#059669' 
                      }}>
                        {(user.credits_remaining || 0).toLocaleString()}
                      </span>
                    </Table.Cell>
                    <Table.Cell>
                      <span style={{ color: '#6b7280' }}>
                        {(user.total_credits_used || 0).toLocaleString()}
                      </span>
                    </Table.Cell>
                    <Table.Cell>
                      <div style={{ fontSize: '12px', color: '#6b7280' }}>
                        {formatDate(user.assigned_at)}
                      </div>
                    </Table.Cell>
                    <Table.Cell>
                      <div style={{ display: 'flex', gap: '8px' }}>
                        <Button
                          onClick={() => handleViewCreditHistory(user)}
                          variant="secondary"
                          icon={<History size={14} />}
                          style={{ fontSize: '12px', padding: '4px 8px' }}
                        >
                          History
                        </Button>
                        <Button
                          onClick={() => handleAdjustCredits(user)}
                          variant="secondary"
                          icon={<DollarSign size={14} />}
                          style={{ fontSize: '12px', padding: '4px 8px' }}
                        >
                          Adjust
                        </Button>
                        <Button
                          onClick={() => handleCancelSubscription(user)}
                          variant="danger"
                          icon={<X size={14} />}
                          style={{ fontSize: '12px', padding: '4px 8px' }}
                        >
                          Cancel
                        </Button>
                      </div>
                    </Table.Cell>
                  </Table.Row>
                );
              })}
            </Table.Body>
          </Table>
        </Card.Content>
      </Card>

      {/* Credit History Modal */}
      {showCreditHistory && (
        <Modal
          isOpen={showCreditHistory}
          onClose={() => setShowCreditHistory(false)}
          title={`Credit History - ${selectedUser?.user_name}`}
          maxWidth="max-w-5xl"
        >
          <div style={{ padding: '20px' }}>
            {creditSummary && (
              <div style={{ 
                background: '#f9fafb',
                padding: '16px',
                borderRadius: '8px',
                marginBottom: '20px',
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))',
                gap: '16px'
              }}>
                <div>
                  <div style={{ fontSize: '12px', color: '#6b7280' }}>Current Balance</div>
                  <div style={{ fontSize: '18px', fontWeight: 'bold', color: '#059669' }}>
                    {(creditSummary.current_balance || 0).toLocaleString()}
                  </div>
                </div>
                <div>
                  <div style={{ fontSize: '12px', color: '#6b7280' }}>Total Granted</div>
                  <div style={{ fontSize: '18px', fontWeight: 'bold', color: '#3b82f6' }}>
                    {(creditSummary.total_granted || 0).toLocaleString()}
                  </div>
                </div>
                <div>
                  <div style={{ fontSize: '12px', color: '#6b7280' }}>Total Used</div>
                  <div style={{ fontSize: '18px', fontWeight: 'bold', color: '#dc2626' }}>
                    {(creditSummary.total_used || 0).toLocaleString()}
                  </div>
                </div>
                <div>
                  <div style={{ fontSize: '12px', color: '#6b7280' }}>Total Refunded</div>
                  <div style={{ fontSize: '18px', fontWeight: 'bold', color: '#f59e0b' }}>
                    {(creditSummary.total_refunded || 0).toLocaleString()}
                  </div>
                </div>
              </div>
            )}

            <Table>
              <Table.Header>
                <Table.Row>
                  <Table.Head>Date</Table.Head>
                  <Table.Head>Type</Table.Head>
                  <Table.Head>Amount</Table.Head>
                  <Table.Head>Balance</Table.Head>
                  <Table.Head>Reason</Table.Head>
                </Table.Row>
              </Table.Header>
              <Table.Body>
                {creditHistory.map((transaction) => (
                  <Table.Row key={transaction.id}>
                    <Table.Cell>
                      <div style={{ fontSize: '12px' }}>
                        {formatDate(transaction.timestamp)}
                      </div>
                    </Table.Cell>
                    <Table.Cell>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                        {getTransactionIcon(transaction.transaction_type)}
                        <span style={{ fontSize: '12px' }}>
                          {transaction.transaction_type.replace('_', ' ').toUpperCase()}
                        </span>
                      </div>
                    </Table.Cell>
                    <Table.Cell>
                      <span style={{ 
                        fontWeight: '500',
                        color: transaction.amount > 0 ? '#059669' : '#dc2626'
                      }}>
                        {transaction.amount > 0 ? '+' : ''}{formatAmount(transaction.amount)}
                      </span>
                    </Table.Cell>
                    <Table.Cell>
                      <span style={{ fontSize: '12px', color: '#6b7280' }}>
                        {(transaction.balance_after || 0).toLocaleString()}
                      </span>
                    </Table.Cell>
                    <Table.Cell>
                      <div style={{ fontSize: '12px', color: '#6b7280', maxWidth: '200px' }}>
                        {transaction.reason}
                      </div>
                    </Table.Cell>
                  </Table.Row>
                ))}
              </Table.Body>
            </Table>
          </div>
        </Modal>
      )}

      {/* Adjust Credits Modal */}
      {showAdjustCredits && (
        <Modal
          isOpen={showAdjustCredits}
          onClose={() => setShowAdjustCredits(false)}
          title={`Adjust Credits - ${selectedUser?.user_name}`}
        >
          <div style={{ padding: '20px' }}>
            <div style={{ marginBottom: '16px' }}>
              <div style={{ fontSize: '14px', color: '#6b7280', marginBottom: '8px' }}>
                Current Balance: <strong>{(selectedUser?.credits_remaining || 0).toLocaleString()}</strong>
              </div>
            </div>

            <FormField
              label="Amount"
              type="number"
              value={adjustmentForm.amount}
              onChange={(e) => setAdjustmentForm({...adjustmentForm, amount: e.target.value})}
              placeholder="Enter amount (positive to add, negative to subtract)"
              required
            />

            <FormField
              label="Reason"
              type="textarea"
              value={adjustmentForm.reason}
              onChange={(e) => setAdjustmentForm({...adjustmentForm, reason: e.target.value})}
              placeholder="Explain why you're adjusting the credits"
              required
              rows={3}
            />

            <div style={{ 
              display: 'flex', 
              justifyContent: 'flex-end', 
              gap: '12px',
              marginTop: '20px'
            }}>
              <Button
                onClick={() => setShowAdjustCredits(false)}
                variant="secondary"
              >
                Cancel
              </Button>
              <Button
                onClick={submitCreditAdjustment}
                disabled={!adjustmentForm.amount || !adjustmentForm.reason}
              >
                Adjust Credits
              </Button>
            </div>
          </div>
        </Modal>
      )}

      {/* Cancel Subscription Confirmation Modal */}
      {showCancelConfirmation && (
        <Modal
          isOpen={showCancelConfirmation}
          onClose={() => setShowCancelConfirmation(false)}
          title={`Cancel Subscription - ${selectedUser?.user_name}`}
          maxWidth="max-w-md"
        >
          <div style={{ padding: '20px' }}>
            <div style={{ 
              background: '#fef2f2',
              border: '1px solid #fecaca',
              borderRadius: '8px',
              padding: '16px',
              marginBottom: '20px'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                <AlertTriangle size={20} color="#dc2626" />
                <h3 style={{ margin: 0, color: '#dc2626', fontSize: '16px' }}>Warning: This action cannot be undone</h3>
              </div>
              <p style={{ margin: 0, color: '#7f1d1d', fontSize: '14px' }}>
                Cancelling this subscription will:
              </p>
              <ul style={{ margin: '8px 0 0 0', paddingLeft: '20px', color: '#7f1d1d', fontSize: '14px' }}>
                <li>Immediately deactivate the subscription</li>
                <li>Forfeit all remaining credits ({(selectedUser?.credits_remaining || 0).toLocaleString()} credits)</li>
                <li>Prevent the user from creating new jobs</li>
                <li>Send a cancellation notification email to the user</li>
              </ul>
            </div>

            <div style={{ marginBottom: '20px' }}>
              <p><strong>User:</strong> {selectedUser?.user_name} ({selectedUser?.user_email})</p>
              <p><strong>Current Plan:</strong> {selectedUser?.current_plan_name}</p>
              <p><strong>Credits to Forfeit:</strong> {(selectedUser?.credits_remaining || 0).toLocaleString()}</p>
            </div>

            <div style={{ 
              display: 'flex', 
              justifyContent: 'flex-end', 
              gap: '12px',
              marginTop: '20px'
            }}>
              <Button
                onClick={() => setShowCancelConfirmation(false)}
                variant="secondary"
                disabled={cancellingSubscription}
              >
                Keep Subscription
              </Button>
              <Button
                onClick={confirmCancelSubscription}
                variant="danger"
                loading={cancellingSubscription}
                disabled={cancellingSubscription}
              >
                {cancellingSubscription ? 'Cancelling...' : 'Cancel Subscription'}
              </Button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
};

export default SubscriptionManagementTab;